"use client";

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';

const navLinks = [
  { href: '/homepage', label: '首頁', labelEn: 'Home' },
  { href: '/products-services', label: '產品與服務', labelEn: 'Products & Services' },
  { href: '/contact', label: '聯絡我們', labelEn: 'Contact' },
];

export default function Header() {
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-md py-3'
          : 'bg-white/90 backdrop-blur-sm py-4'
      }`}
    >
      <div className="max-w-8xl mx-auto px-6 lg:px-10 flex items-center justify-between">
        {/* Logo */}
        <Link href="/homepage" className="flex items-center gap-3 group">
          <AppLogo
            src="/assets/images/image-2026-01-26-180338-1773297202078.png"
            size={48}
            className="transition-transform duration-300 group-hover:scale-105"
          />
          <div className="hidden sm:block">
            <div className="font-display font-800 text-base leading-tight text-primary" style={{ fontWeight: 800 }}>
              醫時醫療有限公司
            </div>
            <div className="font-display text-xs font-600 tracking-wider text-muted uppercase" style={{ fontWeight: 600 }}>
              Easy Medical Limited
            </div>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1">
          {navLinks?.map((link) => {
            const isActive = pathname === link?.href;
            return (
              <Link
                key={link?.href}
                href={link?.href}
                className={`relative px-4 py-2 rounded-lg font-display text-sm font-600 transition-all duration-200 ${
                  isActive
                    ? 'text-primary bg-primary-light' :'text-foreground hover:text-primary hover:bg-primary-light'
                }`}
                style={{ fontWeight: 600 }}
              >
                {link?.label}
                {isActive && (
                  <span className="absolute bottom-0 left-4 right-4 h-0.5 bg-primary rounded-full" />
                )}
              </Link>
            );
          })}
        </nav>

        {/* Right: Email CTA */}
        <div className="hidden lg:flex items-center gap-4">
          <a
            href="mailto:lemonfrancie7@gmail.com"
            className="flex items-center gap-2 text-sm text-muted hover:text-primary transition-colors duration-200"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span className="font-sans text-sm">lemonfrancie7@gmail.com</span>
          </a>
          <Link
            href="/contact"
            className="px-5 py-2.5 rounded-lg font-display text-sm font-700 text-white transition-all duration-200 hover:opacity-90 hover:shadow-glow"
            style={{ background: 'linear-gradient(135deg, #1E5F8E, #2472A4)', fontWeight: 700 }}
          >
            立即聯絡
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden flex flex-col gap-1.5 p-2 rounded-lg hover:bg-primary-light transition-colors"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle navigation menu"
        >
          <span className={`w-6 h-0.5 bg-foreground rounded transition-all duration-300 ${mobileOpen ? 'rotate-45 translate-y-2' : ''}`} />
          <span className={`w-6 h-0.5 bg-foreground rounded transition-all duration-300 ${mobileOpen ? 'opacity-0' : ''}`} />
          <span className={`w-6 h-0.5 bg-foreground rounded transition-all duration-300 ${mobileOpen ? '-rotate-45 -translate-y-2' : ''}`} />
        </button>
      </div>
      {/* Mobile Menu */}
      <div className={`md:hidden overflow-hidden transition-all duration-400 ${mobileOpen ? 'max-h-96' : 'max-h-0'}`}>
        <div className="bg-white border-t border-border px-6 py-4 space-y-1">
          {navLinks?.map((link) => {
            const isActive = pathname === link?.href;
            return (
              <Link
                key={link?.href}
                href={link?.href}
                onClick={() => setMobileOpen(false)}
                className={`block px-4 py-3 rounded-lg font-display text-sm font-600 transition-colors ${
                  isActive ? 'text-primary bg-primary-light' : 'text-foreground hover:text-primary hover:bg-primary-light'
                }`}
                style={{ fontWeight: 600 }}
              >
                <span>{link?.label}</span>
                <span className="ml-2 text-muted text-xs">{link?.labelEn}</span>
              </Link>
            );
          })}
          <div className="pt-3 border-t border-border">
            <a
              href="mailto:lemonfrancie7@gmail.com"
              className="block px-4 py-2 text-sm text-muted"
            >
              lemonfrancie7@gmail.com
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}